import React, { ReactNode } from "react";
import {
  LuArrowDownLeft,
  LuArrowUpRight,
  LuBanknote,
  LuBookOpen,
  LuWallet,
} from "react-icons/lu";

import cx from "../ui/cx";
import { BusinessReportKpisProps } from "../../types/businessReport";

const formatAmount = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const kpiCardClass =
  "rounded-lg bg-card text-card-foreground shadow-sm border border-gray-200 transition-all hover:-translate-y-0.5 hover:border-[#34699A] hover:shadow-md";
const kpiValueClass = "text-xl md:text-2xl font-bold tracking-tight break-words";
const kpiLineClass = "text-base md:text-lg font-bold flex items-center gap-1";
const kpiIconClass = "h-6 w-6";

type KpiCardProps = {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  icon: ReactNode;
  badgeClass: string;
};

const KpiCard = ({ label, value, sub, icon, badgeClass }: KpiCardProps) => (
  <div className={kpiCardClass}>
    <div className="p-4 md:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs md:text-sm text-muted-foreground">{label}</p>
          <div className="mt-1">{value}</div>
          {sub ? (
            <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
          ) : null}
        </div>
        <span className={cx("shrink-0 rounded-xl p-2.5", badgeClass)}>
          {icon}
        </span>
      </div>
    </div>
  </div>
);

const Skeleton = ({ className }: { className: string }) => (
  <div className={cx("animate-pulse rounded-md bg-gray-200", className)} />
);

type Props = {
  kpis: BusinessReportKpisProps | null;
  isLoading: boolean;
};

const BusinessReportKpiCards = ({ kpis, isLoading }: Props) => {
  const showSkeleton = !kpis && isLoading;

  const money = (value: number | undefined, colorClass: string) =>
    showSkeleton ? (
      <Skeleton className="h-7 w-28" />
    ) : (
      <p className={cx(kpiValueClass, colorClass)}>{formatAmount(value)}</p>
    );

  const inOut = (inflow: number | undefined, outflow: number | undefined) =>
    showSkeleton ? (
      <div className="space-y-1">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-24" />
      </div>
    ) : (
      <div className="space-y-0.5">
        <p className={cx(kpiLineClass, "text-emerald-700")}>
          <LuArrowDownLeft className="h-4 w-4" />
          {formatAmount(inflow)}
        </p>
        <p className={cx(kpiLineClass, "text-red-600")}>
          <LuArrowUpRight className="h-4 w-4" />
          {formatAmount(outflow)}
        </p>
      </div>
    );

  const sub = (text: string) => (showSkeleton ? undefined : text);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
      <KpiCard
        label="પેમેન્ટ આવેલ"
        value={money(kpis?.received, "text-emerald-700")}
        icon={<LuArrowDownLeft className={kpiIconClass} />}
        badgeClass="bg-emerald-600/10 text-emerald-700"
      />

      <KpiCard
        label="આવક / જાવક (રોજમેળ)"
        value={inOut(kpis?.daybook.credit, kpis?.daybook.debit)}
        sub={sub(`ચોખ્ખું ${formatAmount(kpis?.daybook.net)}`)}
        icon={<LuBookOpen className={kpiIconClass} />}
        badgeClass="bg-slate-500/10 text-slate-600"
      />

      <KpiCard
        label="રોજમેળ બેલેન્સ"
        value={money(kpis?.daybook.closing, "text-[#34699A]")}
        sub={sub(`ખુલતી ${formatAmount(kpis?.daybook.opening)}`)}
        icon={<LuBanknote className={kpiIconClass} />}
        badgeClass="bg-[#34699A]/10 text-[#34699A]"
      />

      <KpiCard
        label="લેવાના બાકી"
        value={money(kpis?.receivable.closing, "text-emerald-700")}
        sub={sub(`ખુલતી ${formatAmount(kpis?.receivable.opening)}`)}
        icon={<LuWallet className={kpiIconClass} />}
        badgeClass="bg-emerald-600/10 text-emerald-700"
      />
    </div>
  );
};
export default BusinessReportKpiCards;
